import { useMemo } from "react";
import type { Message } from "@ag-ui/client";
import type { ConversationAgentSummary } from "~/services/agents";
import type { TurnFeedback } from "~/services/turns";
import type { RunError } from "./useRunError";
import type { LiveTurnMeta } from "./useStreamingTurnIds";

export interface ToolCallEntry {
  // toolCallId; stable across the call + result pair.
  id: string;
  name: string;
  // Raw JSON arguments string (may be partial while streaming).
  args: string;
  // Tool-result content. `undefined` while the call is still pending — the
  // disclosure renders the Output panel disabled in that state.
  result?: string;
}

// One step of the model's own account of the turn: a text segment it narrated
// between tool calls (`narration`), or a reasoning block it emitted
// (`reasoning`). Tool calls are not part of it — they have their own
// disclosure, and this is the thinking that surrounded them.
export interface TrailEntry {
  kind: "narration" | "reasoning";
  // The AG-UI message id that carried the segment.
  id: string;
  text: string;
}

export interface AssistantTurnData {
  kind: "assistant";
  key: string;
  toolCalls: ToolCallEntry[];
  // What the model said to itself on the way to `text`, in emission order.
  // Agents narrate between steps; each segment used to be overwritten by the
  // next, so a settled turn showed only its last line. They are kept here
  // instead and render as the turn's thinking trail once it settles.
  thinking: TrailEntry[];
  // The turn's answer: the *last* text segment it produced. Every earlier
  // segment is a `narration` entry in `thinking`.
  text: string;
  isRunning: boolean;
  // Set when an assistant message in the turn carried the backend's
  // `cancelled: true` marker (the synthetic "[cancelled]" response written
  // when the user stops a run). Surfaces as an inline "Response cancelled"
  // line on the turn, both live and on reload.
  cancelled: boolean;
  // Set when the run failed: live from the RUN_ERROR event, on reload from the
  // backend's `failed: true` marker. The reason is kept out of `text` so it
  // renders as the turn's failure line rather than as the agent's own output.
  failed: boolean;
  failureMessage?: string;
  // Backend turn ID (== AG-UI run_id == trace_id). Populated from the
  // preceding user message's `turnId` extra (history) or from the live
  // `userMsgIdToTurnId` map keyed by the user message id that triggered
  // the run (live SSE). Undefined while the turn is still running.
  turnId?: string;
  // Existing feedback for this turn, if any (only set from history).
  feedback?: TurnFeedback | null;
  // Turn creation time (ISO). From the `createdAt` extra on the opening user
  // message (history) or the live run-start stamp; undefined until known.
  createdAt?: string;
  // The agent that served this turn, from the opening user message's
  // `turnAgent` extra — set only on shared reads, where a multi-agent thread
  // labels each turn with the agent that served it. Undefined elsewhere; the
  // rendering side falls back to the conversation-level agent.
  agent?: ConversationAgentSummary | null;
}

export interface UserTurnData {
  kind: "user";
  key: string;
  text: string;
  // Backend turn ID (== AG-UI run_id == trace_id) of the turn this message
  // opens. Same value as the following assistant turn's `turnId`; carried here
  // so the user message — the top of the (user, assistant) pair — is the
  // deep-link scroll anchor.
  turnId?: string;
  // IDs of the artifacts the user attached to this message. Populated from the
  // message's `attachmentIds` extra — set optimistically on submit and
  // re-supplied by `/connect` history on reload. Each renders as a card via its
  // id.
  attachmentIds?: string[];
  // Turn creation time (ISO); mirrors the following assistant turn's.
  createdAt?: string;
}

export type Turn = UserTurnData | AssistantTurnData;

type AnyContent =
  | string
  | Array<{ type: string; text?: string } | Record<string, unknown>>
  | null
  | undefined;

function contentToText(content: AnyContent): string {
  if (!content) return "";
  if (typeof content === "string") return content;
  return content
    .map((part) =>
      typeof part === "object" && part && "type" in part && part.type === "text" && "text" in part
        ? String(part.text ?? "")
        : ""
    )
    .join("");
}

// Groups a flat AG-UI message list into per-turn render data.
//
// A turn boundary is a `user` message. Everything between two user messages
// (or after the last user message) forms one assistant turn that aggregates
// all tool calls + the latest assistant text. The same transform runs on both
// the streamed message list and the `/connect` snapshot, so live and replay
// render identically — including the persisted "[cancelled]" marker, which
// is surfaced via the per-message `cancelled` flag and bubbled onto the
// turn so the rendering side can show a "Response cancelled" line.
//
// `userMsgIdToTurnId` carries live-stream associations from the user message
// that triggered a run to its server-side turn_id (captured from
// RunStartedEvent). History-loaded user messages already carry a `turnId`
// extra; the map is the fallback for messages added during the session.
export function useTurns(
  messages: ReadonlyArray<Message>,
  isRunning: boolean,
  liveTurnMeta?: ReadonlyMap<string, LiveTurnMeta>,
  runError?: RunError | null
): Turn[] {
  return useMemo(() => {
    const turns: Turn[] = [];
    let pending: AssistantTurnData | null = null;
    // Latest turn metadata seen on a user message — applied to the
    // assistant turn that follows.
    let pendingTurnId: string | undefined;
    let pendingFeedback: TurnFeedback | null | undefined;
    let pendingCreatedAt: string | undefined;
    let pendingTurnAgent: ConversationAgentSummary | null | undefined;

    // The answer is the turn's *last* text segment; every earlier one stays in
    // the trail. Resolved when the turn closes rather than on arrival, because
    // "last" is only known then — while a run streams, the newest segment sits
    // in the answer slot and joins the trail once the next one arrives.
    const takeAnswer = (turn: AssistantTurnData) => {
      for (let i = turn.thinking.length - 1; i >= 0; i--) {
        const entry = turn.thinking[i];
        if (entry.kind !== "narration") continue;
        turn.text = entry.text;
        turn.thinking.splice(i, 1);
        return;
      }
    };

    const flush = () => {
      if (pending) {
        takeAnswer(pending);
        if (
          pending.toolCalls.length > 0 ||
          pending.text ||
          pending.thinking.length > 0 ||
          pending.cancelled ||
          pending.failed
        ) {
          turns.push(pending);
        }
      }
      pending = null;
    };

    // A turn opens on the first thing the agent emits — an assistant message
    // or a reasoning block, whichever comes first.
    const startTurn = (key: string): AssistantTurnData => ({
      kind: "assistant",
      key,
      toolCalls: [],
      thinking: [],
      text: "",
      isRunning: false,
      cancelled: false,
      failed: false,
      turnId: pendingTurnId,
      feedback: pendingFeedback,
      createdAt: pendingCreatedAt,
      agent: pendingTurnAgent,
    });

    for (const m of messages) {
      if (m.role === "user") {
        flush();
        const extras = m as {
          turnId?: string;
          turnFeedback?: TurnFeedback | null;
          attachmentIds?: string[];
          createdAt?: string;
          turnAgent?: ConversationAgentSummary | null;
        };
        const liveMeta = liveTurnMeta?.get(m.id);
        pendingTurnId = extras.turnId ?? liveMeta?.turnId;
        pendingFeedback = extras.turnFeedback ?? null;
        pendingCreatedAt = extras.createdAt ?? liveMeta?.createdAt;
        pendingTurnAgent = extras.turnAgent;
        turns.push({
          kind: "user",
          key: m.id,
          text: contentToText(m.content as AnyContent),
          turnId: pendingTurnId,
          attachmentIds:
            extras.attachmentIds && extras.attachmentIds.length > 0
              ? extras.attachmentIds
              : undefined,
          createdAt: pendingCreatedAt,
        });
        continue;
      }
      // Reasoning blocks arrive as their own role, and can precede anything
      // else the agent emits.
      if (m.role === "reasoning") {
        pending ??= startTurn(m.id);
        const reasoning = contentToText(m.content as AnyContent);
        if (reasoning) pending.thinking.push({ kind: "reasoning", id: m.id, text: reasoning });
        continue;
      }
      if (m.role === "assistant") {
        pending ??= startTurn(m.id);
        if (m.toolCalls?.length) {
          for (const tc of m.toolCalls) {
            pending.toolCalls.push({
              id: tc.id,
              name: tc.function.name,
              args: tc.function.arguments,
            });
          }
        }
        // The backend sets `cancelled: true` / `failed: true` on the synthetic
        // markers it writes when a run stops or fails. AG-UI's typed schema
        // doesn't model either field, but they survive the JSON round-trip.
        const extras = m as { cancelled?: boolean; failed?: boolean };
        const text = contentToText(m.content as AnyContent);
        if (extras.failed) {
          pending.failed = true;
          if (text) pending.failureMessage = text;
        } else if (text) {
          pending.thinking.push({ kind: "narration", id: m.id, text });
        }
        if (extras.cancelled) pending.cancelled = true;
        continue;
      }
      if (m.role === "tool") {
        if (!pending) continue;
        const entry = pending.toolCalls.find((t) => t.id === m.toolCallId);
        if (entry) entry.result = m.content;
      }
    }
    flush();

    if (isRunning) {
      const last = turns[turns.length - 1];
      if (!last || last.kind === "user") {
        // Fresh user prompt, agent hasn't emitted anything yet — show a
        // placeholder assistant turn so "Thinking..." is visible immediately.
        turns.push({
          kind: "assistant",
          key: "__pending__",
          toolCalls: [],
          thinking: [],
          text: "",
          isRunning: true,
          cancelled: false,
          failed: false,
          turnId: pendingTurnId,
          feedback: pendingFeedback,
          createdAt: pendingCreatedAt,
          agent: pendingTurnAgent,
        });
      } else {
        last.isRunning = true;
      }
    }

    // A live RUN_ERROR lands on the turn that failed, which is where the
    // persisted marker sits after a reload — so the failure doesn't move when
    // the page reloads.
    if (runError) {
      let last = turns[turns.length - 1];
      if (!last || last.kind === "user") {
        last = {
          kind: "assistant",
          key: "__failed__",
          toolCalls: [],
          thinking: [],
          text: "",
          isRunning: false,
          cancelled: false,
          failed: false,
          turnId: pendingTurnId,
          feedback: pendingFeedback,
          createdAt: pendingCreatedAt,
          agent: pendingTurnAgent,
        };
        turns.push(last);
      }
      last.isRunning = false;
      last.failed = true;
      last.failureMessage = runError.message;
      last.turnId = last.turnId ?? runError.runId;
    }

    return turns;
  }, [messages, isRunning, liveTurnMeta, runError]);
}
